/**
 * DevEx Survey content — color-coded metric grid with contributing charts.
 *
 * Scope-decoupled shared component used by both the Management Dashboard
 * DevEx tab and the Metric View DevEx Survey tab. Date filters do not
 * apply; the API returns all available survey quarters.
 */
import { useState, useEffect } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card, CardContent, HoverTip, InfoIcon, Spinner } from '@/components/ui';
import { cn } from '@/lib/utils';
import { portfolioApi } from '@/services/portfolio-api';
import { DEVEX_METRIC_CATEGORIES, getDevExQuestionInfo, type DevExMetricConfig } from '@/lib/devex-metric-categories';
import type {
  DevExMetricKey,
  IDevExHistoryResponse,
  IDevExQuarterPoint,
} from '@skystone/shared';
import { EmptyState } from './empty-state';

type TrendDir = 'up' | 'down' | 'flat' | null;

/**
 * Some survey metrics (e.g. Interrupted, Time Wasted) are inverted: a lower
 * score is better. For colour and trend we mirror the value around the 1–5
 * midpoint (`6 - score`) so an inverted metric reuses the exact same bands and
 * arrows as a normal one — a low raw score then reads as green / improved.
 * The displayed number is always the real, un-mirrored score.
 */
function mirrorIfInverted(score: number | null, isInverted: boolean): number | null {
  if (score === null) return null;
  return isInverted ? 6 - score : score;
}

function scoreColorClasses(score: number | null): string {
  if (score === null) return 'bg-muted text-muted-foreground';
  if (score >= 4) return 'bg-success-100 text-success-700';
  if (score >= 3.5) return 'bg-primary-50 text-primary-700';
  if (score >= 3) return 'bg-warning-100 text-warning-700';
  return 'bg-error-100 text-error-700';
}

function scoreBorderClass(score: number | null): string {
  if (score === null) return 'border-border';
  if (score >= 4) return 'border-success-200';
  if (score >= 3.5) return 'border-primary-100';
  if (score >= 3) return 'border-warning-200';
  return 'border-error-200';
}

function getTrend(current: number | null, previous: number | null): TrendDir {
  if (current === null || previous === null) return null;
  const diff = current - previous;
  if (Math.abs(diff) < 0.15) return 'flat';
  return diff > 0 ? 'up' : 'down';
}

function TrendArrow({ direction }: Readonly<{ direction: TrendDir }>) {
  if (direction === null) return null;
  if (direction === 'up') return <span className="text-success-600 text-[11px]">{'▲'}</span>;
  if (direction === 'down') return <span className="text-error-600 text-[11px]">{'▼'}</span>;
  return <span className="text-muted-foreground text-[11px]">{'→'}</span>;
}

function formatScore(score: number | null): string {
  return score === null ? '—' : score.toFixed(1);
}

/**
 * Directional banner shown at the top of a normal cell (higher is better) or
 * the bottom of an inverted cell (lower is better). Its position is the primary
 * cue; the words remove ambiguity.
 */
function DirectionStrip({ isInverted }: Readonly<{ isInverted: boolean }>) {
  return (
    <div className={cn(
      'flex h-[18px] items-center justify-center gap-0.5 bg-card text-[9px] font-medium text-muted-foreground',
      isInverted ? 'border-t border-border' : 'border-b border-border',
    )}>
      <span aria-hidden="true">{isInverted ? '↓' : '↑'}</span>
      {isInverted ? 'lower is better' : 'higher is better'}
    </div>
  );
}

/**
 * The verbatim survey question + answer scale behind a metric, shown on hover
 * of the small info icon in each card's top-right corner (PRODOPSAI-672). Built
 * on HoverTip (portalled Radix popover) so it escapes the card's `overflow-hidden`
 * and never clips at the grid edge.
 */
function QuestionTip({ question, answers }: Readonly<{ question: string; answers: readonly string[] }>) {
  return (
    <HoverTip
      side="top"
      className="w-64 max-w-none px-3 py-2 normal-case"
      content={
        <div className="space-y-1.5 text-left">
          <p className="text-xs font-medium leading-snug text-popover-foreground">{question}</p>
          <ul className="space-y-0.5">
            {answers.map((answer) => (
              <li key={answer} className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                <span className="inline-block h-1 w-1 flex-shrink-0 rounded-full bg-muted-foreground/60" aria-hidden="true" />
                {answer}
              </li>
            ))}
          </ul>
        </div>
      }
    >
      <span
        className="inline-flex text-muted-foreground/60 transition-colors hover:text-foreground"
        aria-label={`Survey question: ${question}`}
      >
        <InfoIcon className="h-3.5 w-3.5" />
      </span>
    </HoverTip>
  );
}

interface MetricCellProps {
  label: string;
  current: number | null;
  previous: number | null;
  isInverted?: boolean;
  question: string;
  answers: readonly string[];
}

function MetricCell({ label, current, previous, isInverted = false, question, answers }: Readonly<MetricCellProps>) {
  // Colour and trend run on the mirrored value for inverted metrics; the
  // number shown to the user stays the real score. Both ends reserve an equal
  // strip slot so the value stays vertically centred whether the strip sits on
  // top (higher is better) or bottom (lower is better).
  const colorCurrent = mirrorIfInverted(current, isInverted);
  const trend = getTrend(colorCurrent, mirrorIfInverted(previous, isInverted));

  return (
    <div className={cn(
      'relative flex flex-col overflow-hidden rounded-lg border text-center transition-colors',
      scoreBorderClass(colorCurrent),
      scoreColorClasses(colorCurrent),
    )}>
      {/* Sit inside the coloured body (below the 18px direction strip) so the
          icon never straddles the seam between the strip and the card colour. */}
      <div className="absolute right-1 top-5 z-10">
        <QuestionTip question={question} answers={answers} />
      </div>
      {isInverted ? <div className="h-[18px]" aria-hidden="true" /> : <DirectionStrip isInverted={false} />}
      <div className="flex flex-1 flex-col items-center justify-center px-2 py-1.5">
        <span className="text-[10px] font-medium leading-tight">{label}</span>
        <div className="mt-0.5 flex items-center gap-0.5">
          <span className="text-xl font-bold leading-none">{formatScore(current)}</span>
          <TrendArrow direction={trend} />
        </div>
        {previous !== null && (
          <span className="mt-0.5 text-[9px] opacity-60">(prev {formatScore(previous)})</span>
        )}
      </div>
      {isInverted ? <DirectionStrip isInverted /> : <div className="h-[18px]" aria-hidden="true" />}
    </div>
  );
}

// Both scales share the same four colour bands, best-to-worst; only the
// numeric labels differ (the "lower is better" scale reads the bands reversed).
const BAND_SWATCHES: readonly string[] = [
  'bg-success-100 border-success-200',
  'bg-primary-50 border-primary-100',
  'bg-warning-100 border-warning-200',
  'bg-error-100 border-error-200',
];

const HIGHER_LABELS: readonly string[] = ['≥4 strong', '≥3.5 good', '≥3 moderate', '<3 attention'];
const LOWER_LABELS: readonly string[] = ['≤2 strong', '≤2.5 good', '≤3 moderate', '>3 attention'];

function LegendScale({ title, labels }: Readonly<{ title: string; labels: readonly string[] }>) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="w-24 font-medium text-foreground">{title}</span>
      {labels.map((label, i) => (
        <span key={label} className="flex items-center gap-1">
          <span className={cn('inline-block h-2.5 w-2.5 rounded-sm border', BAND_SWATCHES[i])} /> {label}
        </span>
      ))}
    </div>
  );
}

function Legend() {
  return (
    <div className="flex flex-col justify-center gap-1.5 text-[9px] text-muted-foreground h-full px-2">
      <LegendScale title="Higher is better" labels={HIGHER_LABELS} />
      <LegendScale title="Lower is better" labels={LOWER_LABELS} />
      <div className="flex items-center gap-1">
        <span className="text-success-600">{'▲'}</span> <span>Improved</span>
        <span className="ml-1 text-muted-foreground">{'→'}</span> <span>Same</span>
        <span className="ml-1 text-error-600">{'▼'}</span> <span>Declined</span>
      </div>
    </div>
  );
}

function CategorySection({ name, metrics, latestScores, previousScores, showLegend }: Readonly<{
  name: string;
  metrics: readonly DevExMetricConfig[];
  latestScores: Record<DevExMetricKey, number | null> | null;
  previousScores: Record<DevExMetricKey, number | null> | null;
  showLegend?: boolean;
}>) {
  return (
    <div>
      <h3 className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{name}</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5">
        {metrics.map((m) => {
          const info = getDevExQuestionInfo(m.key);
          return (
            <MetricCell
              key={m.key}
              label={m.shortLabel}
              current={latestScores?.[m.key] ?? null}
              previous={previousScores?.[m.key] ?? null}
              isInverted={m.isInverted}
              question={info.question}
              answers={info.answers}
            />
          );
        })}
        {showLegend && <div className="col-span-2"><Legend /></div>}
      </div>
    </div>
  );
}

interface TrendChartConfig {
  title: string;
  name: string;
  fill: string;
  /** Extracts the bar value for a quarter. */
  value: (q: IDevExQuarterPoint) => number;
}

/** The two right-rail trend charts, rendered by a single parameterised chart. */
const TREND_CHARTS: readonly TrendChartConfig[] = [
  { title: 'Contributing teams', name: 'Teams', fill: 'var(--color-chart-2)', value: (q) => q.contributingTeamCount },
  { title: 'Survey responses', name: 'Responses', fill: 'var(--color-chart-4)', value: (q) => q.responseCount },
];

function QuarterBarChart({ quarters, config }: Readonly<{ quarters: IDevExQuarterPoint[]; config: TrendChartConfig }>) {
  if (quarters.length === 0) return <EmptyState />;

  const data = quarters.map((q) => ({ quarter: q.quarter, value: config.value(q) }));

  return (
    <ResponsiveContainer width="100%" height={140}>
      <BarChart data={data} margin={{ top: 5, right: 5, bottom: 0, left: -10 }}>
        <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
        <XAxis dataKey="quarter" tick={{ fontSize: 9 }} className="fill-muted-foreground" />
        <YAxis allowDecimals={false} tick={{ fontSize: 9 }} width={28} className="fill-muted-foreground" />
        <Tooltip />
        <Bar dataKey="value" name={config.name} fill={config.fill} radius={[2, 2, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

export interface DevExSurveyScope {
  region?: string;
  productId?: string;
  teamId?: string;
}

interface DevExSurveyContentProps {
  scope: DevExSurveyScope;
}

export function DevExSurveyContent({ scope }: Readonly<DevExSurveyContentProps>) {
  const [history, setHistory] = useState<IDevExHistoryResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    portfolioApi.getDevExHistory({
      region: scope.region || undefined,
      productId: scope.productId || undefined,
      teamId: scope.teamId || undefined,
    }).then((data) => {
      if (cancelled) return;
      setHistory(data);
      setLoading(false);
    }).catch(() => {
      if (cancelled) return;
      setLoading(false);
    });

    return () => { cancelled = true; };
  }, [scope.region, scope.productId, scope.teamId]);

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Spinner /></div>;
  }

  const quarters = history?.quarters ?? [];
  const suppressedQuarterCount = history?.suppressedQuarterCount ?? 0;

  if (quarters.length === 0) {
    // When every quarter was suppressed by the privacy threshold, explain
    // why rather than showing the generic "no data" message — the data
    // exists but is withheld to protect respondent anonymity and to avoid
    // surfacing unreliable scores from very small samples.
    const message = suppressedQuarterCount > 0
      ? 'DevEx results are hidden for this scope because too few survey responses were collected. Results are only shown once there are at least 3 responses, to protect respondent anonymity and avoid unreliable scores from very small samples.'
      : 'No DevEx survey data available for the selected scope';

    return (
      <div className="space-y-3 p-3">
        <p className="text-xs text-muted-foreground">
          DevEx uses all available survey data. Date filters do not apply on this tab.
        </p>
        <EmptyState message={message} />
      </div>
    );
  }

  const latestQ = quarters[quarters.length - 1];
  const prevQ: IDevExQuarterPoint | undefined = quarters[quarters.length - 2];

  return (
    <div className="space-y-2 p-2">
      <div className="flex flex-col gap-1 px-1 md:flex-row md:items-center md:justify-between">
        <p className="text-[10px] text-muted-foreground">
          DevEx survey data across all quarters. Date filters do not apply.
        </p>
        <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
          <span className="font-semibold text-foreground">{latestQ.quarter}</span>
          <span>{latestQ.contributingTeamCount} teams</span>
          <span>{latestQ.responseCount} responses</span>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 xl:grid-cols-3">
        <Card className="shadow-none xl:col-span-2">
          <CardContent className="space-y-3 p-3">
            {DEVEX_METRIC_CATEGORIES.map((cat, i) => (
              <CategorySection
                key={cat.name}
                name={cat.name}
                metrics={cat.metrics}
                latestScores={latestQ.scores}
                previousScores={prevQ?.scores ?? null}
                showLegend={i === DEVEX_METRIC_CATEGORIES.length - 1 && cat.metrics.length < 4}
              />
            ))}
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:col-span-1 xl:grid-cols-1">
          {TREND_CHARTS.map((chart) => (
            <Card key={chart.title} className="shadow-none flex-1">
              <CardContent className="p-3">
                <h3 className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {chart.title}
                </h3>
                <QuarterBarChart quarters={quarters} config={chart} />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
